import { useCallback, useEffect, useState } from "react";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import type {
  ApplicationApiRow,
  DeploymentEvent,
  NotificationApiRow,
  OrgSummary,
  ProjectApiRow,
} from "src/domains/dashboard/types/DashboardTypes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import { useOrgRealtime } from "../../organization/hooks/useOrgRealtime";

export interface UseDashboardDataResult {
  orgSummary: OrgSummary | null;
  projects: ProjectApiRow[];
  apps: ApplicationApiRow[];
  deploymentEvents: DeploymentEvent[];
  notifications: NotificationApiRow[];
  billing: ReturnType<typeof useOrgRealtime>["billing"];
  billingLoading: boolean;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * useDashboardData — fetches all data the org-scoped dashboard needs in a
 * single parallel batch. Combines:
 *   - GET /api/v1/organizations/:alias/summary (authoritative KPI counts)
 *   - GET /api/v1/projects (recent projects list)
 *   - GET /api/v1/applications (stack status for health metrics)
 *   - GET /api/v1/deployment-events?limit=10 (recent deployments + activity)
 *   - GET /api/v1/notifications (activity feed)
 *   - useOrgRealtime billing (MTD cost)
 */
export function useDashboardData(
  orgAlias: string | undefined,
): UseDashboardDataResult {
  const { billing, billingLoading, refreshStacks, refreshBilling } =
    useOrgRealtime(orgAlias);

  const [orgSummary, setOrgSummary] = useState<OrgSummary | null>(null);
  const [projects, setProjects] = useState<ProjectApiRow[]>([]);
  const [apps, setApps] = useState<ApplicationApiRow[]>([]);
  const [deploymentEvents, setDeploymentEvents] = useState<DeploymentEvent[]>(
    [],
  );
  const [notifications, setNotifications] = useState<NotificationApiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    setError(null);
    try {
      const token = orgSessionManager.getToken() ?? "";
      const headers = { Authorization: `Bearer ${token}` };
      const encodedAlias = encodeURIComponent(orgAlias);

      const [summaryRes, projectsRes, appsRes, eventsRes, notifsRes] =
        await Promise.all([
          fetch(
            `${apiBaseUrls.BACKEND}/api/v1/organizations/${encodedAlias}/summary`,
            {
              headers,
            },
          ),
          fetch(`${apiBaseUrls.BACKEND}/api/v1/projects`, { headers }),
          fetch(`${apiBaseUrls.BACKEND}/api/v1/applications`, { headers }),
          fetch(`${apiBaseUrls.BACKEND}/api/v1/deployment-events?limit=10`, {
            headers,
          }),
          fetch(`${apiBaseUrls.BACKEND}/api/v1/notifications`, { headers }),
        ]);

      // Summary is the authoritative KPI source — treat its failure as fatal.
      if (!summaryRes.ok) throw new Error(`Org summary: ${summaryRes.status}`);
      setOrgSummary((await summaryRes.json()) as OrgSummary);

      // Projects is required for the Recent projects card.
      if (!projectsRes.ok) throw new Error(`Projects: ${projectsRes.status}`);
      setProjects((await projectsRes.json()) as ProjectApiRow[]);

      // Apps, events, and notifications are best-effort — a failure shouldn't
      // block the entire dashboard.
      setApps(
        appsRes.ok ? ((await appsRes.json()) as ApplicationApiRow[]) : [],
      );
      if (eventsRes.ok) {
        const eventsData = (await eventsRes.json()) as {
          events: DeploymentEvent[];
        };
        setDeploymentEvents(eventsData.events ?? []);
      } else {
        setDeploymentEvents([]);
      }
      setNotifications(
        notifsRes.ok ? ((await notifsRes.json()) as NotificationApiRow[]) : [],
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load dashboard data",
      );
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const retry = useCallback(() => {
    void fetchAll();
    void refreshStacks();
    void refreshBilling();
  }, [fetchAll, refreshStacks, refreshBilling]);

  return {
    orgSummary,
    projects,
    apps,
    deploymentEvents,
    notifications,
    billing,
    billingLoading,
    loading,
    error,
    retry,
  };
}
